import {
  APPLICATION_BLOCK_PATTERN_PROFILES,
  APPLICATION_COMPOSITION_METHODOLOGY_V1_CONTRACT,
  APPLICATION_INSTANCE_FIXTURES,
  APPLICATION_LAYOUT_PATTERN_PROFILES,
  APPLICATION_PAGE_PATTERN_PROFILES,
  FIXED_COMPONENT_ONTOLOGY_COMPONENT_TYPES,
  FIXED_DESIGN_SYSTEM_PARTS,
  FORBIDDEN_CORE_PATTERN_DOMAIN_TERMS,
  type ApplicationInstanceFixture,
  type BlockPatternProfile,
  type LayoutPatternProfile,
  type PagePatternProfile,
  validateApplicationCompositionMethodology,
} from "./application-composition-methodology";
import { assessBlockStyleSeparation } from "./lint-block-style-separation";
import { assessReportApplicationShell } from "./lint-report-application-shell";

export const APPLICATION_COMPOSITION_FULL_CLOSURE_SCHEMA_VERSION =
  "cogo.application-composition-full-closure.v1";

export type CompositionPatternKind =
  | "layout-pattern"
  | "ui-block"
  | "page-pattern";

export type CompositionCandidateStatus =
  | "promoted"
  | "candidate"
  | "rejected";

export type CompositionClosureStatus = "pass" | "blocked";

export type ApplicationCompositionWorkstreamId =
  | "W1_REPORT_FIXTURE_BOUNDARY"
  | "W2_PATTERN_MINING_PIPELINE"
  | "W3_PROMOTION_GATE"
  | "W4_DESIGN_SYSTEM_INJECTION"
  | "W5_GRIDONE_PRODUCT_APP_BLUEPRINT"
  | "W6_HARNESS_PRODUCT_SEPARATION"
  | "W7_BROWSER_E2E_PROOF_CONTRACT"
  | "W8_HANDOFF_AND_EVIDENCE";

export interface CompositionMiningSource {
  readonly sourceId: string;
  readonly fixtureId: string;
  readonly sourceKind:
    | "excel-derived-report"
    | "spreadsheet-derived-ops"
    | "admin-dashboard"
    | "publishing-site"
    | "commerce-admin"
    | "knowledge-feed";
  readonly domainLabel: string;
  readonly layoutPatternRefs: readonly string[];
  readonly blockPatternRefs: readonly string[];
  readonly pagePatternRefs: readonly string[];
  readonly componentTypes: readonly string[];
  readonly designSystemParts: readonly string[];
  readonly componentVocabularyExtended: boolean;
  readonly designSystemStructureExtended: boolean;
  readonly proofStages: readonly string[];
}

export interface CompositionPatternCandidate {
  readonly kind: CompositionPatternKind;
  readonly patternId: string;
  readonly supportCount: number;
  readonly sourceIds: readonly string[];
  readonly fixtureIds: readonly string[];
  readonly status: CompositionCandidateStatus;
  readonly failures: readonly string[];
}

export interface CompositionMiningResult {
  readonly schemaVersion: "cogo.application-composition-mining-result.v1";
  readonly sourceCount: number;
  readonly candidateCount: number;
  readonly candidates: readonly CompositionPatternCandidate[];
}

export interface CompositionPromotionGateResult {
  readonly schemaVersion: "cogo.application-composition-promotion-gate.v1";
  readonly minSupport: number;
  readonly status: CompositionClosureStatus;
  readonly promotedPatternCount: number;
  readonly candidatePatternCount: number;
  readonly rejectedPatternCount: number;
  readonly promotedPatternIds: readonly string[];
  readonly rejectedPatternIds: readonly string[];
  readonly candidates: readonly CompositionPatternCandidate[];
}

export interface ReportFixtureBoundaryResult {
  readonly schemaVersion: "cogo.report-fixture-boundary-result.v1";
  readonly status: CompositionClosureStatus;
  readonly classification: string;
  readonly fixtureId: string;
  readonly promotedUiOntology: boolean;
  readonly promotedDesignOntology: boolean;
  readonly publicKgShardBlocked: boolean;
  readonly failures: readonly string[];
}

export interface ProductHarnessSeparationResult {
  readonly schemaVersion: "cogo.product-harness-separation-result.v1";
  readonly status: CompositionClosureStatus;
  readonly productArtifactId: string;
  readonly harnessArtifactId: string;
  readonly productShellStatus: string;
  readonly productStyleStatus: string;
  readonly failures: readonly string[];
}

export interface ApplicationCompositionWorkstreamResult {
  readonly id: ApplicationCompositionWorkstreamId;
  readonly title: string;
  readonly status: CompositionClosureStatus;
  readonly evidence: readonly string[];
  readonly failures: readonly string[];
}

export interface ApplicationCompositionFullClosureProof {
  readonly schemaVersion: typeof APPLICATION_COMPOSITION_FULL_CLOSURE_SCHEMA_VERSION;
  readonly status: CompositionClosureStatus;
  readonly methodologyStatus: CompositionClosureStatus;
  readonly sourceSamples: readonly CompositionMiningSource[];
  readonly mining: CompositionMiningResult;
  readonly promotionGate: CompositionPromotionGateResult;
  readonly reportFixtureBoundary: ReportFixtureBoundaryResult;
  readonly productHarnessSeparation: ProductHarnessSeparationResult;
  readonly browserE2eProofContract: typeof APPLICATION_COMPOSITION_BROWSER_E2E_PROOF_CONTRACT;
  readonly handoffContract: typeof APPLICATION_COMPOSITION_HANDOFF_CONTRACT;
  readonly workstreams: readonly ApplicationCompositionWorkstreamResult[];
}

const REQUIRED_PROMOTION_PROOF_STAGES = [
  "schema-validation",
  "composition-contract-validation",
  "layout-slot-validation",
  "block-page-reference-validation",
  "design-system-separation-validation",
  "headless-render-proof",
] as const;

const ALL_COMPONENT_TYPES = FIXED_COMPONENT_ONTOLOGY_COMPONENT_TYPES;
const ALL_DESIGN_PARTS = FIXED_DESIGN_SYSTEM_PARTS;

export const APPLICATION_COMPOSITION_MINING_SOURCES: readonly CompositionMiningSource[] = [
  {
    sourceId: "gridone-housing-analysis-source",
    fixtureId: "gridone-housing-analysis-app",
    sourceKind: "excel-derived-report",
    domainLabel: "housing price analysis",
    layoutPatternRefs: ["sidebar-workspace", "document-page", "dashboard-grid"],
    blockPatternRefs: [
      "command-header",
      "sidebar-navigation",
      "filter-toolbar",
      "chart-table-composition",
      "document-export-panel",
    ],
    pagePatternRefs: ["admin-workspace-page", "analysis-page"],
    componentTypes: ALL_COMPONENT_TYPES,
    designSystemParts: ALL_DESIGN_PARTS,
    componentVocabularyExtended: false,
    designSystemStructureExtended: false,
    proofStages: REQUIRED_PROMOTION_PROOF_STAGES,
  },
  {
    sourceId: "sheet-operations-scorecard-source",
    fixtureId: "sheet-operations-scorecard-app",
    sourceKind: "spreadsheet-derived-ops",
    domainLabel: "spreadsheet operations scorecard",
    layoutPatternRefs: ["sidebar-workspace", "dashboard-grid", "grid"],
    blockPatternRefs: [
      "command-header",
      "sidebar-navigation",
      "filter-toolbar",
      "chart-table-composition",
      "data-grid-with-actions",
    ],
    pagePatternRefs: ["admin-workspace-page", "analysis-page"],
    componentTypes: ALL_COMPONENT_TYPES,
    designSystemParts: ALL_DESIGN_PARTS,
    componentVocabularyExtended: false,
    designSystemStructureExtended: false,
    proofStages: REQUIRED_PROMOTION_PROOF_STAGES,
  },
  {
    sourceId: "public-data-publication-source",
    fixtureId: "public-data-publication-app",
    sourceKind: "publishing-site",
    domainLabel: "public data publication",
    layoutPatternRefs: ["multi-column", "document-page", "dashboard-grid"],
    blockPatternRefs: [
      "command-header",
      "content-list",
      "chart-table-composition",
      "document-export-panel",
    ],
    pagePatternRefs: ["content-index-page", "analysis-page"],
    componentTypes: ALL_COMPONENT_TYPES,
    designSystemParts: ALL_DESIGN_PARTS,
    componentVocabularyExtended: false,
    designSystemStructureExtended: false,
    proofStages: REQUIRED_PROMOTION_PROOF_STAGES,
  },
  {
    sourceId: "transaction-admin-source",
    fixtureId: "transaction-admin-app",
    sourceKind: "commerce-admin",
    domainLabel: "transaction admin",
    layoutPatternRefs: ["sidebar-workspace", "grid", "split"],
    blockPatternRefs: [
      "command-header",
      "sidebar-navigation",
      "filter-toolbar",
      "data-grid-with-actions",
      "detail-summary",
    ],
    pagePatternRefs: ["admin-workspace-page", "transaction-flow-page"],
    componentTypes: ALL_COMPONENT_TYPES,
    designSystemParts: ALL_DESIGN_PARTS,
    componentVocabularyExtended: false,
    designSystemStructureExtended: false,
    proofStages: REQUIRED_PROMOTION_PROOF_STAGES,
  },
  {
    sourceId: "knowledge-feed-review-source",
    fixtureId: "knowledge-feed-review-app",
    sourceKind: "knowledge-feed",
    domainLabel: "knowledge feed review",
    layoutPatternRefs: ["feed", "multi-column", "stack"],
    blockPatternRefs: ["command-header", "content-list", "detail-summary"],
    pagePatternRefs: ["content-index-page", "detail-page"],
    componentTypes: ALL_COMPONENT_TYPES,
    designSystemParts: ALL_DESIGN_PARTS,
    componentVocabularyExtended: false,
    designSystemStructureExtended: false,
    proofStages: REQUIRED_PROMOTION_PROOF_STAGES,
  },
] as const;

export const REPORT_FIXTURE_BOUNDARY_CONTRACT = {
  schemaVersion: "cogo.report-fixture-boundary-contract.v1",
  fixtureClassification: "application-instance-fixture",
  coreOntologyPromotionAllowed: false,
  promotedUiOntologyAllowed: false,
  promotedDesignOntologyAllowed: false,
  publishedKgShardAllowed: false,
  fixtureTtlNames: [
    "backoffice-report-application-v1.ttl",
    "gridone-backoffice-report-application-v1.ttl",
  ],
  requiredClassificationFixtureId: "gridone-housing-analysis-app",
} as const;

export const APPLICATION_COMPOSITION_BROWSER_E2E_PROOF_CONTRACT = {
  schemaVersion: "cogo.application-composition-browser-e2e-proof-contract.v1",
  proofCommand: "pnpm agentic:cogo-housing-browser-e2e",
  productBrowserProofCommand: "pnpm agentic:cogo-housing-product-report-browser-proof",
  sourceFirstProofCommand: "pnpm agentic:cogo-housing-ci-source-first-proof",
  requiredGateIds: [
    "B0 source-first-cogo-binding-plan-consumed",
    "B1 local-csv-source-integrity",
    "B2 headless-cogo-runtime-smoke",
    "B3 chart-source-row-parity",
    "B4 workbook-chart-extraction-parity",
    "B4b excel-workbook-chartpart-surface-parity",
    "B5 all-chart-panels-rendered",
    "B6 all-source-table-dom-value-parity",
    "B7 all-report-section-surfaces-rendered",
    "B8 document-export-parity",
    "B9 no-gridone-domain-policy",
  ],
  requiredChartSurfaceCount: 23,
  requiredTableSurfaceCount: 22,
  requiredReportSectionCount: 40,
  mixedBarLineComboChartRequired: true,
  screenshotEvidenceRequired: true,
  browserRenderRequired: true,
} as const;

export const APPLICATION_COMPOSITION_HANDOFF_CONTRACT = {
  schemaVersion: "cogo.application-composition-handoff-contract.v1",
  methodologyDoc:
    "docs/agentic/COGO-APPLICATION-COMPOSITION-ONTOLOGY-METHODOLOGY-2026-06-26.md",
  closureDoc:
    "docs/agentic/COGO-APPLICATION-COMPOSITION-FULL-CLOSURE-2026-06-26.md",
  proofArtifact:
    "docs/agentic/evidence/application-composition-full-closure-proof.json",
  studioConsumes: [
    "methodologyContract",
    "fixtureClassification",
    "promotionGate",
    "styleSeparation",
    "reportApplicationShell",
    "browserE2eProofContract",
  ],
  studioMustNotDo: [
    "promote report/Gridone/domain labels into component vocabulary",
    "embed raw visual styles in generated Cogo UI",
    "treat validation harness output as a user-facing product application",
    "publish without evidence packet status",
  ],
} as const;

export function mineApplicationCompositionCandidates(
  sources: readonly CompositionMiningSource[] = APPLICATION_COMPOSITION_MINING_SOURCES,
): CompositionMiningResult {
  const byKey = new Map<string, {
    kind: CompositionPatternKind;
    patternId: string;
    sourceIds: Set<string>;
    fixtureIds: Set<string>;
  }>();

  const add = (kind: CompositionPatternKind, patternId: string, source: CompositionMiningSource) => {
    const key = `${kind}:${patternId}`;
    const existing = byKey.get(key) ?? {
      kind,
      patternId,
      sourceIds: new Set<string>(),
      fixtureIds: new Set<string>(),
    };
    existing.sourceIds.add(source.sourceId);
    existing.fixtureIds.add(source.fixtureId);
    byKey.set(key, existing);
  };

  for (const source of sources) {
    for (const patternId of source.layoutPatternRefs) add("layout-pattern", patternId, source);
    for (const patternId of source.blockPatternRefs) add("ui-block", patternId, source);
    for (const patternId of source.pagePatternRefs) add("page-pattern", patternId, source);
  }

  const candidates = [...byKey.values()]
    .map((candidate) => ({
      kind: candidate.kind,
      patternId: candidate.patternId,
      supportCount: candidate.fixtureIds.size,
      sourceIds: [...candidate.sourceIds].sort(),
      fixtureIds: [...candidate.fixtureIds].sort(),
      status: "candidate" as const,
      failures: [],
    }))
    .sort((a, b) => `${a.kind}:${a.patternId}`.localeCompare(`${b.kind}:${b.patternId}`));

  return {
    schemaVersion: "cogo.application-composition-mining-result.v1",
    sourceCount: sources.length,
    candidateCount: candidates.length,
    candidates,
  };
}

export function evaluateApplicationCompositionPromotion(params: {
  readonly mining?: CompositionMiningResult;
  readonly sources?: readonly CompositionMiningSource[];
  readonly minSupport?: number;
} = {}): CompositionPromotionGateResult {
  const sources = params.sources ?? APPLICATION_COMPOSITION_MINING_SOURCES;
  const mining = params.mining ?? mineApplicationCompositionCandidates(sources);
  const minSupport = params.minSupport ?? 3;
  const sourceById = new Map(sources.map((source) => [source.sourceId, source]));

  const candidates = mining.candidates.map((candidate) => {
    const failures = promotionFailures(candidate, sourceById, minSupport);
    const status: CompositionCandidateStatus = failures.some((failure) => !failure.startsWith("support "))
      ? "rejected"
      : candidate.supportCount >= minSupport
        ? "promoted"
        : "candidate";
    return { ...candidate, status, failures };
  });

  const promoted = candidates.filter((candidate) => candidate.status === "promoted");
  const rejected = candidates.filter((candidate) => candidate.status === "rejected");

  return {
    schemaVersion: "cogo.application-composition-promotion-gate.v1",
    minSupport,
    status: rejected.length === 0 && promoted.length > 0 ? "pass" : "blocked",
    promotedPatternCount: promoted.length,
    candidatePatternCount: candidates.filter((candidate) => candidate.status === "candidate").length,
    rejectedPatternCount: rejected.length,
    promotedPatternIds: promoted.map((candidate) => `${candidate.kind}:${candidate.patternId}`).sort(),
    rejectedPatternIds: rejected.map((candidate) => `${candidate.kind}:${candidate.patternId}`).sort(),
    candidates,
  };
}

export function validateReportFixtureBoundary(params: {
  readonly classification?: Record<string, unknown>;
  readonly publicKgManifestEntryNames?: readonly string[];
} = {}): ReportFixtureBoundaryResult {
  const classification = params.classification ?? {
    classification: "application-instance-fixture",
    fixtureId: "gridone-housing-analysis-app",
    promotedUiOntology: false,
    promotedDesignOntology: false,
  };
  const manifestNames = new Set(params.publicKgManifestEntryNames ?? []);
  const failures: string[] = [];
  const fixtureShardLeaks = REPORT_FIXTURE_BOUNDARY_CONTRACT.fixtureTtlNames.filter((name) =>
    manifestNames.has(name),
  );

  if (classification.classification !== REPORT_FIXTURE_BOUNDARY_CONTRACT.fixtureClassification) {
    failures.push("Gridone/report classification must remain application-instance-fixture.");
  }
  if (classification.fixtureId !== REPORT_FIXTURE_BOUNDARY_CONTRACT.requiredClassificationFixtureId) {
    failures.push("Gridone/report classification must point to gridone-housing-analysis-app.");
  }
  if (classification.promotedUiOntology !== false) {
    failures.push("Gridone/report must not be promoted as core UI ontology.");
  }
  if (classification.promotedDesignOntology !== false) {
    failures.push("Gridone/report must not be promoted as design ontology.");
  }
  if (fixtureShardLeaks.length > 0) {
    failures.push(`Report fixture TTLs must not be published KG runtime shards: ${fixtureShardLeaks.join(", ")}.`);
  }

  return {
    schemaVersion: "cogo.report-fixture-boundary-result.v1",
    status: failures.length === 0 ? "pass" : "blocked",
    classification: String(classification.classification ?? ""),
    fixtureId: String(classification.fixtureId ?? ""),
    promotedUiOntology: classification.promotedUiOntology === true,
    promotedDesignOntology: classification.promotedDesignOntology === true,
    publicKgShardBlocked: fixtureShardLeaks.length === 0,
    failures,
  };
}

export function buildGridoneReportProductApplicationFixture() {
  return {
    schemaVersion: "cogo.product-application.v1",
    artifactId: "gridone-housing-report-product-app",
    applicationKind: "cogo.report-application",
    targetAudience: "user-facing",
    productDomain: "cogo.xyz",
    designSystem: {
      id: "reb-rone-report-application-design-system.v1",
      injectionMode: "design-system-value-set",
      uiOwns: "structure-semantics-data-actionflow",
      designSystemOwns: "visual-surface-color-radius-typography-density",
    },
    reportApplication: {
      schemaVersion: "cogo.report-application.v1",
      applicationKind: "source-first-backoffice-report",
      fixtureClassification: "application-instance-fixture",
      layoutPatternRefs: ["sidebar-workspace", "document-page", "dashboard-grid"],
      pagePatternRefs: ["admin-workspace-page", "analysis-page"],
      blockPatternRefs: [
        "command-header",
        "sidebar-navigation",
        "filter-toolbar",
        "chart-table-composition",
        "document-export-panel",
      ],
      validationHarnessArtifactId: "gridone-housing-validation-harness",
    },
    ui: {
      root: {
        id: "gridone-product-shell",
        type: "container",
        role: "application-shell",
        semanticRole: "cogo.report-application user-facing",
        className: "@ds:app_shell",
        designRole: "app-shell",
        children: [
          {
            id: "global-command-header",
            type: "container",
            role: "global-command-header",
            semanticRole: "global-command-header",
            className: "@ds:global_command_header",
            designRole: "global-command-header",
            children: [
              {
                id: "webmcp-command-input",
                type: "input",
                role: "webmcp chat command-input",
                semanticRole: "webmcp chat command-input ask-cogo",
                className: "@ds:command_input",
                designRole: "command-input",
                placeholder: "Ask Cogo",
              },
              {
                id: "report-common-controls",
                type: "container",
                role: "report-common-controls",
                semanticRole: "report-common-controls period-filter frequency-toggle report-mode-selector",
                className: "@ds:report_common_controls",
                designRole: "report-common-controls",
                children: [
                  {
                    id: "control-monthly",
                    type: "button",
                    role: "report-mode-selector",
                    className: "@ds:control_button_active",
                    designRole: "control-button",
                    text: "Monthly",
                  },
                  {
                    id: "control-weekly",
                    type: "button",
                    role: "frequency-toggle",
                    className: "@ds:control_button",
                    designRole: "control-button",
                    text: "Weekly",
                  },
                ],
              },
            ],
          },
          {
            id: "workspace",
            type: "container",
            role: "workspace",
            className: "@ds:workspace",
            designRole: "workspace",
            children: [
              {
                id: "left-report-nav",
                type: "nav",
                role: "sidebar report-nav",
                semanticRole: "collapsible sidebar full-height navigation-rail",
                collapsible: true,
                resizable: true,
                fullHeight: true,
                className: "@ds:sidebar",
                designRole: "sidebar-navigation",
                children: [
                  {
                    id: "nav-summary",
                    type: "button",
                    role: "sidebar-nav-item",
                    className: "@ds:sidebar_nav_item_active",
                    designRole: "sidebar-nav-item",
                    text: "Summary",
                  },
                ],
              },
              {
                id: "a4-report-canvas",
                type: "container",
                role: "a4-report-canvas report-canvas",
                semanticRole: "a4-report-canvas report-canvas",
                pageFormat: "A4",
                paperSize: "A4",
                orientation: "portrait",
                className: "@ds:a4_canvas",
                designRole: "a4-report-canvas",
                children: [
                  {
                    id: "report-narrative",
                    type: "text",
                    role: "report-narrative insight executive-summary",
                    semanticRole: "report-narrative insight",
                    className: "@ds:narrative",
                    designRole: "report-narrative",
                    text: "Source-backed narrative explains housing price movements before charts and tables.",
                  },
                  {
                    id: "main-mixed-bar-line-chart",
                    type: "chart",
                    role: "chart-surface combo-chart mixed-bar-line",
                    semanticRole: "chart-surface mixed-bar-line combo-chart",
                    className: "@ds:chart_surface",
                    designRole: "chart-surface",
                    chartKind: "combo",
                    dataRef: "appData.previewChartRows",
                  },
                  {
                    id: "main-source-data-table",
                    type: "data_table",
                    role: "data-table report-table source-backed",
                    semanticRole: "data-table report-table",
                    className: "@ds:data_table",
                    designRole: "data-table",
                    dataRef: "appData.previewTableRows",
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  };
}

export function buildGridoneValidationHarnessFixture() {
  return {
    schemaVersion: "cogo.validation-harness.v1",
    artifactId: "gridone-housing-validation-harness",
    targetAudience: "validator",
    isValidationHarness: true,
    proves: [
      "formula_cell_golden_parity",
      "browser_chart_render_parity",
      "all_table_browser_dom_parity",
      "document_export_parity",
    ],
  };
}

export function validateProductHarnessSeparation(params: {
  readonly product?: ReturnType<typeof buildGridoneReportProductApplicationFixture>;
  readonly harness?: ReturnType<typeof buildGridoneValidationHarnessFixture>;
} = {}): ProductHarnessSeparationResult {
  const product = params.product ?? buildGridoneReportProductApplicationFixture();
  const harness = params.harness ?? buildGridoneValidationHarnessFixture();
  const productRoot = product.ui.root;
  const shell = assessReportApplicationShell(product);
  const style = assessBlockStyleSeparation({ ui: { root: productRoot } }, {
    strict: true,
    requireDesignAuthority: true,
  });
  const failures: string[] = [];

  if (product.artifactId === harness.artifactId) failures.push("Product and validation harness artifacts must be separate.");
  if (product.targetAudience !== "user-facing") failures.push("Product report application must target user-facing usage.");
  if (harness.isValidationHarness !== true) failures.push("Validation harness artifact must be explicitly marked as a harness.");
  if (shell.status !== "pass") failures.push(...shell.findings.map((finding) => finding.message));
  if (style.status !== "pass") failures.push(...style.findings.map((finding) => finding.message));

  return {
    schemaVersion: "cogo.product-harness-separation-result.v1",
    status: failures.length === 0 ? "pass" : "blocked",
    productArtifactId: product.artifactId,
    harnessArtifactId: harness.artifactId,
    productShellStatus: shell.status,
    productStyleStatus: style.status,
    failures,
  };
}

export function buildApplicationCompositionFullClosureProof(params: {
  readonly classification?: Record<string, unknown>;
  readonly publicKgManifestEntryNames?: readonly string[];
  readonly sources?: readonly CompositionMiningSource[];
} = {}): ApplicationCompositionFullClosureProof {
  const methodology = validateApplicationCompositionMethodology();
  const sources = params.sources ?? APPLICATION_COMPOSITION_MINING_SOURCES;
  const mining = mineApplicationCompositionCandidates(sources);
  const promotionGate = evaluateApplicationCompositionPromotion({ mining, sources });
  const reportFixtureBoundary = validateReportFixtureBoundary({
    classification: params.classification,
    publicKgManifestEntryNames: params.publicKgManifestEntryNames,
  });
  const productHarnessSeparation = validateProductHarnessSeparation();
  const workstreams = buildWorkstreamResults({
    methodologyPass: methodology.ok,
    mining,
    promotionGate,
    reportFixtureBoundary,
    productHarnessSeparation,
  });
  const blocked = workstreams.filter((workstream) => workstream.status === "blocked");

  return {
    schemaVersion: APPLICATION_COMPOSITION_FULL_CLOSURE_SCHEMA_VERSION,
    status: blocked.length === 0 ? "pass" : "blocked",
    methodologyStatus: methodology.ok ? "pass" : "blocked",
    sourceSamples: sources,
    mining,
    promotionGate,
    reportFixtureBoundary,
    productHarnessSeparation,
    browserE2eProofContract: APPLICATION_COMPOSITION_BROWSER_E2E_PROOF_CONTRACT,
    handoffContract: APPLICATION_COMPOSITION_HANDOFF_CONTRACT,
    workstreams,
  };
}

function promotionFailures(
  candidate: CompositionPatternCandidate,
  sourceById: Map<string, CompositionMiningSource>,
  minSupport: number,
): string[] {
  const failures: string[] = [];
  if (candidate.supportCount < minSupport) {
    failures.push(`support ${candidate.supportCount} is below promotion threshold ${minSupport}`);
  }
  assertNoDomainTerm(candidate.patternId, failures);
  if (!canonicalPattern(candidate.kind, candidate.patternId)) {
    failures.push(`${candidate.kind}:${candidate.patternId} is not in the canonical methodology corpus.`);
  }
  for (const sourceId of candidate.sourceIds) {
    const source = sourceById.get(sourceId);
    if (!source) {
      failures.push(`source ${sourceId} is missing from mining source corpus.`);
      continue;
    }
    if (source.componentVocabularyExtended) failures.push(`${sourceId} extends component vocabulary.`);
    if (source.designSystemStructureExtended) failures.push(`${sourceId} extends design system structure.`);
    if (!sameSet(source.componentTypes, FIXED_COMPONENT_ONTOLOGY_COMPONENT_TYPES)) {
      failures.push(`${sourceId} component vocabulary differs from fixed Cogo component ontology.`);
    }
    if (!sameSet(source.designSystemParts, FIXED_DESIGN_SYSTEM_PARTS)) {
      failures.push(`${sourceId} design-system structure differs from fixed Cogo design-system parts.`);
    }
    for (const stage of REQUIRED_PROMOTION_PROOF_STAGES) {
      if (!source.proofStages.includes(stage)) failures.push(`${sourceId} is missing proof stage ${stage}.`);
    }
  }
  return failures;
}

function canonicalPattern(kind: CompositionPatternKind, patternId: string):
  | LayoutPatternProfile
  | BlockPatternProfile
  | PagePatternProfile
  | undefined {
  if (kind === "layout-pattern") {
    const profile = APPLICATION_LAYOUT_PATTERN_PROFILES.find((pattern) => pattern.patternId === patternId);
    return profile && profile.slots.length > 0 && profile.constraints.length > 0 ? profile : undefined;
  }
  if (kind === "ui-block") {
    const profile = APPLICATION_BLOCK_PATTERN_PROFILES.find((pattern) => pattern.patternId === patternId);
    return profile &&
      profile.requiredComponents.length > 0 &&
      profile.dataInputs.length > 0 &&
      profile.actionOutputs.length > 0
      ? profile
      : undefined;
  }
  const profile = APPLICATION_PAGE_PATTERN_PROFILES.find((pattern) => pattern.patternId === patternId);
  return profile && profile.requiredBlockPatternRefs.length > 0 ? profile : undefined;
}

function buildWorkstreamResults(params: {
  methodologyPass: boolean;
  mining: CompositionMiningResult;
  promotionGate: CompositionPromotionGateResult;
  reportFixtureBoundary: ReportFixtureBoundaryResult;
  productHarnessSeparation: ProductHarnessSeparationResult;
}): readonly ApplicationCompositionWorkstreamResult[] {
  return [
    workstream({
      id: "W1_REPORT_FIXTURE_BOUNDARY",
      title: "Existing report/Gridone ontology is classified as fixture, not core ontology.",
      pass: params.reportFixtureBoundary.status === "pass",
      evidence: [
        "packages/cogo-validator/ontology/gridone-backoffice-report-contract-v1.json",
        "scripts/check-publish-shard-fresh.mjs UNPUBLISHED fixture boundary",
      ],
      failures: params.reportFixtureBoundary.failures,
    }),
    workstream({
      id: "W2_PATTERN_MINING_PIPELINE",
      title: "Pattern mining emits candidate layout/block/page patterns from multiple app sources.",
      pass: params.mining.sourceCount >= 5 && params.mining.candidateCount >= 10,
      evidence: ["mineApplicationCompositionCandidates()", "APPLICATION_COMPOSITION_MINING_SOURCES"],
      failures: params.mining.sourceCount >= 5 ? [] : ["Mining needs at least five source samples."],
    }),
    workstream({
      id: "W3_PROMOTION_GATE",
      title: "Promotion gate requires repeated, source-backed, non-domain-specific patterns.",
      pass: params.promotionGate.status === "pass" && params.promotionGate.promotedPatternCount >= 6,
      evidence: ["evaluateApplicationCompositionPromotion()"],
      failures: params.promotionGate.rejectedPatternIds,
    }),
    workstream({
      id: "W4_DESIGN_SYSTEM_INJECTION",
      title: "Design-system injection is enforced; Cogo UI carries structure and @ds authority only.",
      pass: params.productHarnessSeparation.productStyleStatus === "pass",
      evidence: ["assessBlockStyleSeparation(strict)", "STYLE_SEPARATION_GENERATOR_CONTRACT"],
      failures: params.productHarnessSeparation.productStyleStatus === "pass"
        ? []
        : params.productHarnessSeparation.failures,
    }),
    workstream({
      id: "W5_GRIDONE_PRODUCT_APP_BLUEPRINT",
      title: "Gridone report application blueprint is user-facing and follows composition methodology.",
      pass: params.productHarnessSeparation.productShellStatus === "pass",
      evidence: ["buildGridoneReportProductApplicationFixture()", "assessReportApplicationShell()"],
      failures: params.productHarnessSeparation.productShellStatus === "pass"
        ? []
        : params.productHarnessSeparation.failures,
    }),
    workstream({
      id: "W6_HARNESS_PRODUCT_SEPARATION",
      title: "Validation harness remains a separate artifact from the user-facing product app.",
      pass: params.productHarnessSeparation.status === "pass",
      evidence: ["validateProductHarnessSeparation()"],
      failures: params.productHarnessSeparation.failures,
    }),
    workstream({
      id: "W7_BROWSER_E2E_PROOF_CONTRACT",
      title: "Browser E2E proof contract covers chart/table/section parity and mixed combo charts.",
      pass: APPLICATION_COMPOSITION_BROWSER_E2E_PROOF_CONTRACT.requiredChartSurfaceCount === 23 &&
        APPLICATION_COMPOSITION_BROWSER_E2E_PROOF_CONTRACT.requiredTableSurfaceCount === 22 &&
        APPLICATION_COMPOSITION_BROWSER_E2E_PROOF_CONTRACT.requiredReportSectionCount === 40 &&
        APPLICATION_COMPOSITION_BROWSER_E2E_PROOF_CONTRACT.mixedBarLineComboChartRequired,
      evidence: [
        APPLICATION_COMPOSITION_BROWSER_E2E_PROOF_CONTRACT.proofCommand,
        APPLICATION_COMPOSITION_BROWSER_E2E_PROOF_CONTRACT.productBrowserProofCommand,
      ],
      failures: [],
    }),
    workstream({
      id: "W8_HANDOFF_AND_EVIDENCE",
      title: "Downstream Studio/platform sessions receive explicit contract and evidence paths.",
      pass: APPLICATION_COMPOSITION_HANDOFF_CONTRACT.studioConsumes.length >= 6,
      evidence: [
        APPLICATION_COMPOSITION_HANDOFF_CONTRACT.methodologyDoc,
        APPLICATION_COMPOSITION_HANDOFF_CONTRACT.closureDoc,
        APPLICATION_COMPOSITION_HANDOFF_CONTRACT.proofArtifact,
      ],
      failures: [],
    }),
  ].map((item) => params.methodologyPass ? item : {
    ...item,
    status: "blocked" as const,
    failures: [...item.failures, "Application composition methodology validation failed."],
  });
}

function workstream(input: {
  id: ApplicationCompositionWorkstreamId;
  title: string;
  pass: boolean;
  evidence: readonly string[];
  failures: readonly string[];
}): ApplicationCompositionWorkstreamResult {
  return {
    id: input.id,
    title: input.title,
    status: input.pass ? "pass" : "blocked",
    evidence: input.evidence,
    failures: input.pass ? [] : input.failures,
  };
}

function assertNoDomainTerm(patternId: string, failures: string[]) {
  for (const term of FORBIDDEN_CORE_PATTERN_DOMAIN_TERMS) {
    if (patternId.includes(term)) failures.push(`${patternId} promotes forbidden domain term ${term}.`);
  }
}

function sameSet(actual: readonly string[], expected: readonly string[]): boolean {
  const actualSet = new Set(actual);
  const expectedSet = new Set(expected);
  if (actualSet.size !== expectedSet.size) return false;
  return [...expectedSet].every((item) => actualSet.has(item));
}

export function applicationInstanceFixtureById(fixtureId: string): ApplicationInstanceFixture | undefined {
  return APPLICATION_INSTANCE_FIXTURES.find((fixture) => fixture.fixtureId === fixtureId);
}
